import { c as _c } from "react/compiler-runtime";
import { type ConversationDto } from '@shared/all/domains/messages';
import { useEffect, useEffectEvent } from 'react';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import socket from '../socket/socket';
import { useConversationActions } from './useConversations';
import useConversationsQuery from './useConversationsQuery';
import useSocket, { type ActivityStatus, type MessageSender, type NewConversationPayload } from './useSocket';
import useUser from './useUser';
type RealtimeBridgeOptions = {
  client: WrappedTRPCClient;
  service: {
    refreshIfNeeded: () => Promise<unknown>;
  };
  isAuthenticated: boolean | null;
  currentUserId: number;
  initializeUser: () => unknown;
  logout: () => unknown;
  /** A message the server pushes to show the user. */
  showServerMessage: (message: string) => void;
  /** Tell the user about a tron reward (the balance is already updated). */
  showTronReward: (quantity: number, title: string) => void;
  /** Platform extra on a notification count (web: the Electron badge). */
  onNotificationCount?: (count: number, detail?: {
    type: string;
    username: string;
  }) => void;
  /**
   * Platform extra on a new message in a main transcript, after the store has
   * it (web: the unread badge and the Electron notification).
   */
  onNewMessage?: (sender: MessageSender, conversationId: string, message: string) => void;
  /**
   * Platform socket wiring that lives and dies with the connection (web: the
   * desktop OneDrive relay). Returns its cleanup.
   */
  connectExtras?: () => () => void;
};

/**
 * The app's one realtime connection, shared by web and mobile Main: loads the
 * conversation list into the conversations store, connects the socket for the
 * session, and routes what arrives over it (user events, messages, typing,
 * presence, membership) into the user and the conversations store.
 *
 * Listeners register once per auth state. The handlers are Effect Events, so
 * they always see the current props without the listeners being torn down and
 * registered again on every render.
 */
const useRealtimeBridge = options => {
  const $ = _c(62);
  const {
    addTrons,
    setPendingNotificationsCount
  } = useUser();
  const {
    onNewMessage,
    setUserTyping,
    setConversations,
    updateActivityStatus,
    addConversations,
    removeConversation,
    rejectDisputeConversation,
    handleMemberRemoved,
    updateMessage,
    markMessageDeleted,
    markMessageRestored
  } = useConversationActions();
  const {
    onMessage,
    onRefreshUser,
    onTronReward,
    onLogout,
    onNotificationCount,
    onActivity,
    onConversationDeleted,
    onBanDisputeRejected,
    onMemberRemoved,
    onTyping,
    onNewMessage: onNewMessageSocket,
    onMessageEdited,
    onMessageDeleted,
    onMessageRestored,
    onNewConversation
  } = useSocket();
  const {
    data,
    dataUpdatedAt
  } = useConversationsQuery(options.client);
  let t0;
  if ($[0] !== data || $[1] !== setConversations) {
    t0 = () => {
      if (data) {
        setConversations(data.pages.flatMap(_temp));
      }
    };
    $[0] = data;
    $[1] = setConversations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== data || $[4] !== dataUpdatedAt) {
    t1 = [data, dataUpdatedAt];
    $[3] = data;
    $[4] = dataUpdatedAt;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  if ($[6] !== options) {
    t2 = message => {
      options.showServerMessage(message);
    };
    $[6] = options;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleServerMessage = useEffectEvent(t2);
  let t3;
  if ($[8] !== options) {
    t3 = () => {
      options.initializeUser();
    };
    $[8] = options;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleRefreshUser = useEffectEvent(t3);
  let t4;
  if ($[10] !== addTrons || $[11] !== options) {
    t4 = (quantity, title) => {
      addTrons(quantity);
      options.showTronReward(quantity, title);
    };
    $[10] = addTrons;
    $[11] = options;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleTronReward = useEffectEvent(t4);
  let t5;
  if ($[13] !== options) {
    t5 = () => {
      options.logout();
    };
    $[13] = options;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleLogout = useEffectEvent(t5);
  let t6;
  if ($[15] !== options || $[16] !== setPendingNotificationsCount) {
    t6 = (count, detail) => {
      setPendingNotificationsCount(count);
      options.onNotificationCount?.(count, detail);
    };
    $[15] = options;
    $[16] = setPendingNotificationsCount;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleNotificationCount = useEffectEvent(t6);
  let t7;
  if ($[18] !== onNewMessage || $[19] !== options) {
    t7 = (sender, convId, message_0, messageId, createdAt, attachments, parentId) => {
      if (parentId) {
        return;
      }
      const senderWithActivity = {
        ...sender,
        lastActive: new Date(),
        isActive: true
      };
      onNewMessage(senderWithActivity, convId, message_0, messageId, createdAt, attachments, sender.id === options.currentUserId);
      options.onNewMessage?.(sender, convId, message_0);
    };
    $[18] = onNewMessage;
    $[19] = options;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleNewMessage = useEffectEvent(t7);
  let t8;
  if ($[21] !== handleMemberRemoved || $[22] !== options.currentUserId) {
    t8 = (conversationId, memberId) => {
      handleMemberRemoved(conversationId, memberId, options.currentUserId);
    };
    $[21] = handleMemberRemoved;
    $[22] = options.currentUserId;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleMemberRemovedEvent = useEffectEvent(t8);
  let t9;
  if ($[24] !== options) {
    t9 = () => options.connectExtras?.() ?? _temp2;
    $[24] = options;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const handleConnectExtras = useEffectEvent(t9);
  let t10;
  if ($[26] !== addConversations || $[27] !== handleConnectExtras || $[28] !== handleLogout || $[29] !== handleMemberRemovedEvent || $[30] !== handleNewMessage || $[31] !== handleNotificationCount || $[32] !== handleRefreshUser || $[33] !== handleServerMessage || $[34] !== handleTronReward || $[35] !== markMessageDeleted || $[36] !== markMessageRestored || $[37] !== onActivity || $[38] !== onBanDisputeRejected || $[39] !== onConversationDeleted || $[40] !== onLogout || $[41] !== onMemberRemoved || $[42] !== onMessage || $[43] !== onMessageDeleted || $[44] !== onMessageEdited || $[45] !== onMessageRestored || $[46] !== onNewConversation || $[47] !== onNewMessageSocket || $[48] !== onNotificationCount || $[49] !== onRefreshUser || $[50] !== onTronReward || $[51] !== onTyping || $[52] !== options.isAuthenticated || $[53] !== options.service || $[54] !== rejectDisputeConversation || $[55] !== removeConversation || $[56] !== setUserTyping || $[57] !== updateActivityStatus || $[58] !== updateMessage) {
    t10 = () => {
      if (options.isAuthenticated === null) {
        return;
      }
      options.service.refreshIfNeeded().then(_temp3);
      const cleanups = [onMessage(message_1 => handleServerMessage(message_1)), onRefreshUser(() => handleRefreshUser()), onTronReward((quantity_0, title_0) => handleTronReward(quantity_0, title_0)), onLogout(() => handleLogout()), onNotificationCount((count_0, detail_0) => handleNotificationCount(count_0, detail_0)), handleConnectExtras(), onTyping((convId_0, senderId) => {
        setUserTyping(convId_0, senderId);
      }), onNewMessageSocket((...t11) => {
        const args = t11;
        return handleNewMessage(...args);
      }), onMessageEdited((messageId_0, convId_1, content) => {
        updateMessage(convId_1, messageId_0, content);
      }), onMessageDeleted((messageId_1, convId_2) => {
        markMessageDeleted(convId_2, messageId_1);
      }), onMessageRestored((messageId_2, convId_3, content_0, attachments_0) => {
        markMessageRestored(convId_3, messageId_2, content_0, attachments_0);
      }), onNewConversation(payload => {
        const conversationDto = {
          ...payload,
          type: payload.type ?? "REGULAR",
          ban: payload.ban ?? null,
          unreadCount: payload.unreadCount ?? 0,
          muted: payload.muted ?? false,
          kind: payload.kind ?? "DM",
          slug: null,
          topic: null,
          isPublic: true,
          space: null,
          factiii: null,
          participants: payload.participants.map(_temp4)
        };
        addConversations([conversationDto]);
      }), onActivity((status, convId_4) => {
        if (status.lastActive) {
          updateActivityStatus(status.userId, status.lastActive, status.isActive, convId_4);
        }
      }), onConversationDeleted(conversationId_0 => {
        removeConversation(conversationId_0);
      }), onBanDisputeRejected(conversationId_1 => {
        rejectDisputeConversation(conversationId_1);
      }), onMemberRemoved((conversationId_2, memberId_0) => handleMemberRemovedEvent(conversationId_2, memberId_0))];
      return () => {
        socket.disconnect();
        cleanups.forEach(_temp5);
      };
    };
    $[26] = addConversations;
    $[27] = handleConnectExtras;
    $[28] = handleLogout;
    $[29] = handleMemberRemovedEvent;
    $[30] = handleNewMessage;
    $[31] = handleNotificationCount;
    $[32] = handleRefreshUser;
    $[33] = handleServerMessage;
    $[34] = handleTronReward;
    $[35] = markMessageDeleted;
    $[36] = markMessageRestored;
    $[37] = onActivity;
    $[38] = onBanDisputeRejected;
    $[39] = onConversationDeleted;
    $[40] = onLogout;
    $[41] = onMemberRemoved;
    $[42] = onMessage;
    $[43] = onMessageDeleted;
    $[44] = onMessageEdited;
    $[45] = onMessageRestored;
    $[46] = onNewConversation;
    $[47] = onNewMessageSocket;
    $[48] = onNotificationCount;
    $[49] = onRefreshUser;
    $[50] = onTronReward;
    $[51] = onTyping;
    $[52] = options.isAuthenticated;
    $[53] = options.service;
    $[54] = rejectDisputeConversation;
    $[55] = removeConversation;
    $[56] = setUserTyping;
    $[57] = updateActivityStatus;
    $[58] = updateMessage;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  let t11;
  if ($[60] !== options.isAuthenticated) {
    t11 = [options.isAuthenticated];
    $[60] = options.isAuthenticated;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  useEffect(t10, t11);
};
export default useRealtimeBridge;
function _temp(p) {
  return p.conversations;
}
function _temp2() {}
function _temp3() {
  return socket.connect();
}
function _temp4(p_0) {
  return {
    ...p_0,
    lastActive: p_0.lastActive ?? new Date(),
    isActive: p_0.isActive
  };
}
function _temp5(cleanup) {
  return cleanup();
}